import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Mail, Phone, MapPin, Globe, Users, FileText } from 'lucide-react';

export default function ContactPage() {
  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">Contact Us</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get in touch with the ACP/LDC Sugar Industries Group. We welcome collaboration, 
            inquiries, and engagement from all stakeholders interested in sugar industry development.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle>Send us a Message</CardTitle>
                <CardDescription>
                  Fill out the form below and we'll get back to you as soon as possible.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="form-control">
                      <Label htmlFor="firstName">First Name</Label>
                      <Input id="firstName" placeholder="Your first name" className='input-form-control'/>
                    </div>
                    <div className="form-control">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input id="lastName" placeholder="Your last name" className='input-form-control'/>
                    </div>
                  </div>
                  
                  <div className="form-control">
                    <Label htmlFor="email">Email Address</Label>
                    <Input id="email" type="email" placeholder="your.email@example.com" className='input-form-control'/>
                  </div>
                  
                  <div className="form-control"> 
                    <Label htmlFor="organization">Organization</Label>
                    <Input id="organization" placeholder="Your organization or company" className='input-form-control'/>
                  </div>
                  
                  <div className="form-control">
                    <Label htmlFor="country">Country</Label>
                    <Input id="country" placeholder="Your country" className='input-form-control'/>
                  </div>
                  
                  <div className="form-control">
                    <Label htmlFor="subject">Subject</Label>
                    <Input id="subject" placeholder="Brief description of your inquiry"className='input-form-control' />
                  </div>
                  
                  <div className="form-control">
                    <Label htmlFor="message">Message</Label>
                    <Textarea 
                      id="message" 
                      placeholder="Please provide details about your inquiry or how we can help you"
                      rows={5}className='input-form-control'
                    />
                  </div>
                  
                  <Button type="submit" className="w-full action bg-amber-600 hover:bg-amber-700 py-5">
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            {/* Primary Contact */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 font-semibold text-yellow-00">
                  <span>Primary Contact</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span>info@acpsugar.org</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <span>+44 (0) 20 7XXX XXXX</span>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="w-4 h-4 text-gray-500 mt-1" />
                  <div>
                    <p>ACP/LDC Sugar Industries Group</p>
                    <p>London, United Kingdom</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Specialized Contacts */}
            <Card>
              <CardHeader>
                <CardTitle className=' font-semibold'>Specialized Inquiries</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Users className="w-4 h-4 text-amber-600 mt-1" />
                  <div>
                    <p className="font-medium">Membership & Partnership</p>
                    <p className="text-sm text-gray-600">membership@acpsugar.org</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FileText className="w-4 h-4 text-amber-600 mt-1" />
                  <div>
                    <p className="font-medium">Research & Publications</p>
                    <p className="text-sm text-gray-600">research@acpsugar.org</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Globe className="w-4 h-4 text-amber-600 mt-1" />
                  <div>
                    <p className="font-medium">Trade & Policy</p>
                    <p className="text-sm text-gray-600">policy@acpsugar.org</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Regional Representatives */}
            <Card>
              <CardHeader>
                <CardTitle>Regional Representatives</CardTitle>
                <CardDescription>
                  Connect with our regional representatives for location-specific inquiries.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="font-medium">Africa Region</p>
                  <p className="text-sm text-gray-600">africa@acpsugar.org</p>
                </div>
                <div>
                  <p className="font-medium">Caribbean Region</p>
                  <p className="text-sm text-gray-600">caribbean@acpsugar.org</p>
                </div>
                <div>
                  <p className="font-medium">Pacific Region</p>
                  <p className="text-sm text-gray-600">pacific@acpsugar.org</p>
                </div>
              </CardContent>
            </Card>

            {/* Office Hours */}
            <Card>
              <CardHeader>
                <CardTitle>Office Hours</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Monday - Friday:</span>
                    <span>9:00 AM - 5:00 PM GMT</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday:</span>
                    <span>Closed</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday:</span>
                    <span>Closed</span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mt-4">
                  We typically respond to inquiries within 48 hours during business days.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Additional Information */}
        <section className="mt-16 bg-gray-50 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">How Can We Help?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <Users className="w-12 h-12 text-amber-600 mx-auto mb-4" />
              <h3 className="font-bold text-gray-900 mb-2">Membership</h3>
              <p className="text-gray-600 text-sm">
                Information about joining our group, membership benefits, and requirements for sugar industry organizations.
              </p>
            </div>
            <div className="text-center">
              <FileText className="w-12 h-12 text-amber-600 mx-auto mb-4" />
              <h3 className="font-bold text-gray-900 mb-2">Research & Data</h3>
              <p className="text-gray-600 text-sm">
                Access to industry research, market intelligence, and statistical data on sugar production and trade.
              </p>
            </div>
            <div className="text-center">
              <Globe className="w-12 h-12 text-amber-600 mx-auto mb-4" />
              <h3 className="font-bold text-gray-900 mb-2">Advocacy & Policy</h3>
              <p className="text-gray-600 text-sm">
                Support for trade policy advocacy, market access issues, and engagement with international organizations.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}